import {MenuItemDef, GetContextMenuItemsParams} from "@ag-grid-community/core";
import {ManifestDetailsContext} from "../ManifestDetailsV2";
import {
  isCompleted,
  isJobAlreadySplit,
  isPartiallyCompletedJob,
  isStopsAreSameJob
} from "../../../../utils/stops/StopUtils";
import {TManifestStopEnhanced} from "../../../common/types";
import {getSnoozeIndicatorMenuItemDef} from "./SnoozeIndicatorMenuItem";
import {getDistinctJobIds, resolveTargetStops} from "../../../../utils/stops/adjustStopTime.utils";

export enum GridMenuContextText {
  UnassignJobs = "Unassign Jobs",
  UnassignJob = "Unassign Job",
  MarkStopComplete = "Mark stop complete",
  MarkStopIncomplete = "Mark stop incomplete",
  ReassignJob = "Reassign Job",
  ReassignJobs = "Reassign Jobs",
  ReassignPartlyCompleteJob = "Reassign Partly Complete Job",
  AdjustJobStopTime = "Adjust Job's Stop Time",
  MassAdjustJobStopTime = "Mass Adjustment of Job's Stop Time",
  MultipleJobsSelected = "Multiple Jobs Selected",
  SplitJob = "Split Job"
}

export const disabledWhenCanUnassignPartialJob = (
  selectedStops: TManifestStopEnhanced[],
  manifestStops: TManifestStopEnhanced[]
) => {
  const selectedJobIds = [...new Set(selectedStops.map((ms) => ms.job.jobId))];
  const jobsBySelectedIds = new Map();
  selectedJobIds.forEach((jobId) => {
    const allStopsByJobIds = manifestStops.filter((stop) => stop.job.jobId === jobId);
    jobsBySelectedIds.set(jobId, allStopsByJobIds);
  });

  for (const [, stops] of jobsBySelectedIds.entries()) {
    if (stops.every(isCompleted)) {
      return true; // If all stops for this job are completed, return true
    }
  }
  return false; // If no job has all stops completed, return false
};

const getUnassignJobMenuItemDef = (
  params: GetContextMenuItemsParams<TManifestStopEnhanced, ManifestDetailsContext>
): MenuItemDef => {
  const manifestDetailsContext = params.context as ManifestDetailsContext;
  if (manifestDetailsContext.selectedStops.length >= 1) {
    const selectedJobIds = manifestDetailsContext.selectedStops.map((ms) => ms.job.jobId);
    const arrayOfJobStopIds = manifestDetailsContext.selectedStops.map((ms) => ms.jobStopId);
    const arrayOfJobNumbers = manifestDetailsContext.selectedStops.map((ms) => ms.job.jobNumber);
    const findAllJobContainsCompleted = manifestDetailsContext.manifest.stops?.filter((ms) => {
      return selectedJobIds.includes(ms.job.jobId) && isCompleted(ms);
    });

    const disableUnassignJobs = manifestDetailsContext.canUnassignPartialJob
      ? disabledWhenCanUnassignPartialJob(
          manifestDetailsContext.selectedStops,
          manifestDetailsContext.manifest.stops as TManifestStopEnhanced[]
        )
      : findAllJobContainsCompleted?.length !== 0;
    return {
      name:
        manifestDetailsContext.selectedStops.length > 1 && !isStopsAreSameJob(manifestDetailsContext.selectedStops)
          ? GridMenuContextText.UnassignJobs
          : GridMenuContextText.UnassignJob,
      action: () =>
        manifestDetailsContext.handleUnassignJob({
          jobStopIds: arrayOfJobStopIds,
          jobNumbers: arrayOfJobNumbers,
          jobIds: selectedJobIds
        }),
      disabled: disableUnassignJobs
    };
  } else {
    const jobId = params.node?.data?.job.jobId;
    const jobStopId = params.node?.data?.jobStopId;
    let unassignJobDisabled = false;
    if (jobId && jobStopId) {
      unassignJobDisabled = manifestDetailsContext.canUnassignPartialJob
        ? disabledWhenCanUnassignPartialJob(
            [params?.node?.data as TManifestStopEnhanced],
            manifestDetailsContext.manifest.stops as TManifestStopEnhanced[]
          )
        : (manifestDetailsContext.manifest?.stops ?? []).some((ms) => {
            return ms.job.jobId === jobId && isCompleted(ms);
          });
    }
    return {
      name: GridMenuContextText.UnassignJob,
      action: () =>
        manifestDetailsContext.handleUnassignJob({
          jobStopIds: [(params?.node?.data?.jobStopId as number) ?? 0],
          jobNumbers: [(params?.node?.data?.job.jobNumber as string) ?? ""],
          jobIds: [jobId as number]
        }),
      disabled: unassignJobDisabled
    };
  }
};

const getMarkJobCompleteMenuItemDef = (
  params: GetContextMenuItemsParams<TManifestStopEnhanced, ManifestDetailsContext>
): MenuItemDef => {
  return getMarkJobMenuItemDef(
    params,
    (jobStopId, disabled) => {
      return {
        name: GridMenuContextText.MarkStopComplete,
        action: () => (params.context as ManifestDetailsContext).handleMarkJobStopComplete(jobStopId),
        disabled: disabled
      };
    },
    (manifestStop: TManifestStopEnhanced, jobStopId: number) => {
      return manifestStop.jobStopId === jobStopId && isCompleted(manifestStop);
    }
  );
};

const getMarkJobInCompleteMenuItemDef = (
  params: GetContextMenuItemsParams<TManifestStopEnhanced, ManifestDetailsContext>
): MenuItemDef => {
  return getMarkJobMenuItemDef(
    params,
    (jobStopId, disabled) => {
      return {
        name: GridMenuContextText.MarkStopIncomplete,
        action: () => (params.context as ManifestDetailsContext).handleMarkJobStopIncomplete(jobStopId),
        disabled: disabled
      };
    },
    (manifestStop: TManifestStopEnhanced, jobStopId: number) => {
      return manifestStop.jobStopId === jobStopId && !isCompleted(manifestStop);
    }
  );
};

const getReassignJobMenuItemDef = (
  params: GetContextMenuItemsParams<TManifestStopEnhanced, ManifestDetailsContext>
): MenuItemDef => {
  const manifestDetailsContext = params.context as ManifestDetailsContext;
  if (manifestDetailsContext.selectedStops.length >= 1) {
    let disableReassign = false;
    const selectedJobIds = manifestDetailsContext.selectedStops.map((ms) => ms.job.jobId);

    selectedJobIds.forEach((id) => {
      if (disableReassign) return;
      const correspondedJobStops = manifestDetailsContext.manifest.stops?.filter((stop) => stop.job.jobId === id);
      if (correspondedJobStops?.every(isCompleted) || isJobAlreadySplit(correspondedJobStops)) {
        disableReassign = true;
      }
    });

    let name = GridMenuContextText.ReassignJobs;
    if (isStopsAreSameJob(manifestDetailsContext.selectedStops)) {
      name = GridMenuContextText.ReassignJob;
      const correspondedJobStops = manifestDetailsContext.manifest.stops?.filter(
        (stop) => stop.job.jobId === manifestDetailsContext.selectedStops[0].job.jobId
      );
      if (isPartiallyCompletedJob({stops: correspondedJobStops} as any)) {
        name = GridMenuContextText.ReassignPartlyCompleteJob;
      }
    }

    return {
      name: name,
      action: () => manifestDetailsContext.handleReassignJob(),
      disabled: disableReassign
    };
  } else {
    let disableReassign = false;
    const jobId = params.node?.data?.job.jobId;
    const selectedJobStops = (manifestDetailsContext.manifest?.stops ?? []).filter((ms) => {
      return ms.job.jobId === jobId;
    });
    disableReassign = selectedJobStops.every((stop) => isCompleted(stop)) || isJobAlreadySplit(selectedJobStops);
    return {
      name: isPartiallyCompletedJob({stops: selectedJobStops} as any)
        ? GridMenuContextText.ReassignPartlyCompleteJob
        : GridMenuContextText.ReassignJob,
      action: () => manifestDetailsContext.handleReassignJob(params.node?.data as TManifestStopEnhanced),
      disabled: disableReassign
    };
  }
};

const getSplitJobMenuItemDef = (
  params: GetContextMenuItemsParams<TManifestStopEnhanced, ManifestDetailsContext>
): MenuItemDef => {
  const manifestDetailsContext = params.context as ManifestDetailsContext;
  const targetJobId =
    manifestDetailsContext.selectedStops.length >= 1
      ? manifestDetailsContext.selectedStops.map((ms) => ms.job.jobId)[0]
      : params.node?.data?.job.jobId;

  const allJobStops = manifestDetailsContext.manifest.stops?.filter((stop) => stop.job.jobId === targetJobId) ?? [];
  return {
    name: GridMenuContextText.SplitJob,
    action: () => manifestDetailsContext.handleSplitJob(targetJobId!),
    disabled:
      allJobStops.every((stop) => isCompleted(stop)) ||
      allJobStops.every((stop) => !isCompleted(stop)) ||
      isJobAlreadySplit(allJobStops)
  };
};

const getAdjustTimeJobMenuItemDef = (
  params: GetContextMenuItemsParams<TManifestStopEnhanced, ManifestDetailsContext>
): MenuItemDef => {
  const context = params.context as ManifestDetailsContext;
  // Right-click acts on the clicked stop's job; a multi-selection acts on every selected job.
  const targetJobIds = getDistinctJobIds(resolveTargetStops(context.selectedStops, params.node?.data));
  const stopsOfJobs =
    context.manifest.stops?.filter((stop: TManifestStopEnhanced) => targetJobIds.includes(stop.job.jobId)) ?? [];
  const isMassAdjust = targetJobIds.length > 1;

  return {
    name: isMassAdjust ? GridMenuContextText.MassAdjustJobStopTime : GridMenuContextText.AdjustJobStopTime,
    action: () => context.handleAdjustTimeJob(stopsOfJobs),
    disabled: stopsOfJobs.length === 0
  };
};

const getSnoozeIndicatorMenuItemDefFromParams = (
  params: GetContextMenuItemsParams<TManifestStopEnhanced, ManifestDetailsContext>
): MenuItemDef | undefined => {
  const {backendColorRulesEnabled, handleSnoozeIndicator, selectedStops} = params.context;
  if (!backendColorRulesEnabled || !handleSnoozeIndicator) {
    return undefined;
  }
  // DP-3070: a multi-selection snoozes every selected stop, otherwise the right-clicked one — the
  // same targets as Adjust Job's Stop Time.
  const targets = resolveTargetStops(selectedStops, params.node?.data);
  if (targets.length === 0) {
    return undefined;
  }
  // Only coloured stops can be snoozed, and a snoozed stop has no colour until the snooze ends. The
  // colour is read from each stop's current row, not from selectedStops: the selection keeps the row
  // objects from when it was made, while the drawer's snooze overlay gives a just-snoozed stop a new,
  // uncoloured row.
  const anyUncoloured = targets.some((stop) => !params.api.getRowNode(String(stop.jobStopId))?.data?.indicatorColor);
  return getSnoozeIndicatorMenuItemDef({
    disabled: anyUncoloured,
    onSnooze: (minutes) => handleSnoozeIndicator(targets, minutes)
  });
};

const getMarkJobMenuItemDef = (
  params: GetContextMenuItemsParams<TManifestStopEnhanced, ManifestDetailsContext>,
  menuItemSupplier: (jobStopId: number, disabled: boolean) => MenuItemDef,
  disabledCheck: (manifestStop: TManifestStopEnhanced, jobStopId: number) => boolean
): MenuItemDef => {
  const manifestDetailsContext = params.context as ManifestDetailsContext;
  const jobStopId = params.node?.data?.jobStopId;
  let markCompleteDisabled = false;
  if (jobStopId) {
    markCompleteDisabled = (manifestDetailsContext.manifest?.stops ?? []).some((js: TManifestStopEnhanced) =>
      disabledCheck(js, jobStopId!)
    );
  }
  return menuItemSupplier(jobStopId!, markCompleteDisabled);
};

export const ManifestDetailsContextMenu = (
  params: GetContextMenuItemsParams<TManifestStopEnhanced, ManifestDetailsContext>
): (string | MenuItemDef)[] => {
  if (!params.node) return [];
  const result: (string | MenuItemDef)[] = [];
  if (params.context.selectedStops.length > 1 && !isStopsAreSameJob(params.context.selectedStops)) {
    result.push({name: GridMenuContextText.MultipleJobsSelected, disabled: true, cssClasses: ["menu-title"]});
  }
  result.push(getUnassignJobMenuItemDef(params));
  if (params.context) {
    if (params.context.selectedStops.length <= 1) {
      result.push(getMarkJobCompleteMenuItemDef(params));
      result.push(getMarkJobInCompleteMenuItemDef(params));
    }
    // Adjust/Mass Adjust is offered for both single- and multi-job selections.
    result.push(getAdjustTimeJobMenuItemDef(params));
    result.push(getReassignJobMenuItemDef(params));
    if (params.context.isSplitJobEnabled) {
      if (isStopsAreSameJob(params.context.selectedStops)) {
        result.push(getSplitJobMenuItemDef(params));
      }
    }
    const snoozeItem = getSnoozeIndicatorMenuItemDefFromParams(params);
    if (snoozeItem) {
      result.push(snoozeItem);
    }
  }

  return result;
};
